import React, { useContext, useEffect } from 'react';
import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomPagination from '../../uiParts/CustomPagination';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import { ChangeEvent, useState } from 'react';
import { gray100, primaryColor } from '../../../constants/Colors';
import SideBar from '../../uiParts/SideBar';
import { sideBarWidth } from '../../../constants/Location';
import { useNavigate, useParams } from 'react-router-dom';
import { MemberNewsItemList } from './MemberNewsItems';
import GeneralNewsItemList from './GeneralNewsItems';
import { MaterialNewsItemList } from './MaterialNewsItems';
import { AuthContext } from '../../../contexts/auth';
import CustomHead from '../CustomHead';

export type News = {
  id: number;
  title: string;
  date: string;
  tag?: string[];
  image?: string;
};

const styles = {
  wrapper: {
    '@media screen and (max-width:600px)': {
      margin: '24px 16px'
    },
    margin: '32px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px',
      mb: '16px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: 500,
    mb: '24px'
  },
  tabs: {
    borderBottom: '1px solid #DBDBE0'
  },
  tab: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    '&.Mui-selected': {
      color: primaryColor
    },
    padding: '9px 16px',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.4px'
  }
};

const NewsList = (): React.ReactElement => {
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');

  const { type } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const navigate = useNavigate();

  const tabValue = (() => {
    switch (type) {
      case 'member':
        return 1;
      case 'material':
        return 2;
      default:
        return 0;
    }
  })();

  const newsList = (() => {
    switch (type) {
      case 'member':
        return (
          <MemberNewsItemList
            currentPage={currentPage}
            setErrorText={setErrorText}
            setLoading={setLoading}
            setOpen={setOpen}
            setTotalPages={setTotalPages}
          />
        );
      case 'material':
        return (
          <MaterialNewsItemList
            currentPage={currentPage}
            setErrorText={setErrorText}
            setLoading={setLoading}
            setOpen={setOpen}
            setTotalPages={setTotalPages}
          />
        );
      default:
        return (
          <GeneralNewsItemList
            currentPage={currentPage}
            setErrorText={setErrorText}
            setLoading={setLoading}
            setOpen={setOpen}
            setTotalPages={setTotalPages}
          />
        );
    }
  })();

  const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleTabChange = (event: ChangeEvent<unknown>, value: number) => {
    switch (value) {
      case 0:
        navigate('/news');
        break;
      case 1:
        navigate('/news/member');
        break;
      case 2:
        navigate('/news/material');
        break;
      default:
        break;
    }
    setCurrentPage(1);
    setTotalPages(0);
  };

  // ログインチェック
  const { checkLoginStatus } = useContext(AuthContext);
  useEffect((): void => {
    void (async (): Promise<void> => {
      if (!checkLoginStatus) {
        navigate('/auth/login');
        return;
      }
      const result = await checkLoginStatus();
      if (!result.check) {
        navigate('/auth/login');
        return;
      }
    })();
  }, []);

  const title = ((): string => {
    switch (type) {
      case 'member':
        return '会員個別情報';
      case 'material':
        return '新着建材情報';
      default:
        return '一般情報';
    }
  })();

  return (
    <>
      <CustomHead title={`お知らせ ${title} | CMCOS[コムコス]`} />
      <Grid>
        <CustomLoading loading={loading} />
        <SideBar />
        <Grid
          sx={{
            '@media screen and (min-width:961px)': {
              ml: sideBarWidth
            }
          }}
        >
          <Box sx={styles.wrapper}>
            <CustomLoading loading={false} />
            <Typography sx={styles.title}>お知らせ</Typography>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={styles.tabs}
              indicatorColor="primary"
            >
              <Tab label="一般情報" sx={styles.tab} />
              <Tab label="会員個別情報" sx={styles.tab} />
              <Tab label="新着建材情報" sx={styles.tab} />
            </Tabs>
            {newsList}
            {totalPages > 1 ? (
              <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
            ) : null}
            <CustomSnackbar
              open={open}
              autoHideDuration={6000}
              onClose={() => setOpen(false)}
              text={errorText}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default NewsList;
